import React, { useState, useEffect } from 'react';
import { IoClose, IoAdd, IoRemove, IoCart, IoFlash, IoTime } from 'react-icons/io5';
import { toast } from 'react-toastify';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Time Picker Modal Component
const TimePickerModal = ({
  isOpen,
  onClose,
  onConfirm,
  currentTime = '12:00',
  title = 'Select Time',
}) => {
  const [selectedTime, setSelectedTime] = useState(currentTime);

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5);
  const periods = ['AM', 'PM'];

  const [currentHour, currentMinute] = selectedTime.split(':');
  const currentPeriod = parseInt(currentHour) >= 12 ? 'PM' : 'AM';
  const displayHour = parseInt(currentHour) % 12 || 12;

  const handleHourChange = (hour) => {
    const actualHour =
      currentPeriod === 'PM' ? (hour === 12 ? 12 : hour + 12) : hour === 12 ? 0 : hour;
    setSelectedTime(`${actualHour.toString().padStart(2, '0')}:${currentMinute}`);
  };

  const handleMinuteChange = (minute) => {
    setSelectedTime(`${currentHour}:${minute.toString().padStart(2, '0')}`);
  };

  const handlePeriodChange = (period) => {
    const currentHourNum = parseInt(currentHour);
    let newHour = currentHourNum;

    if (period === 'AM' && currentHourNum >= 12) {
      newHour = currentHourNum - 12;
    } else if (period === 'PM' && currentHourNum < 12) {
      newHour = currentHourNum + 12;
    }

    setSelectedTime(`${newHour.toString().padStart(2, '0')}:${currentMinute}`);
  };

  const handleConfirm = () => {
    onConfirm(selectedTime);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60]">
      <div className="bg-white p-6 rounded-2xl shadow-2xl w-80 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors duration-200"
        >
          <IoClose size={20} />
        </button>

        <h3 className="text-lg font-semibold mb-4 pr-6 text-gray-800">{title}</h3>

        <div className="flex justify-between mb-6 space-x-3">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-2 text-gray-600">Hour</label>
            <select
              value={displayHour}
              onChange={(e) => handleHourChange(parseInt(e.target.value))}
              className="w-full p-2 border border-gray-300 rounded-lg text-gray-800"
            >
              {hours.map((hour) => (
                <option key={hour} value={hour}>
                  {hour}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium mb-2 text-gray-600">Minute</label>
            <select
              value={currentMinute}
              onChange={(e) => handleMinuteChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-gray-800"
            >
              {minutes.map((minute) => (
                <option key={minute} value={minute.toString().padStart(2, '0')}>
                  {minute.toString().padStart(2, '0')}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium mb-2 text-gray-600">AM/PM</label>
            <select
              value={currentPeriod}
              onChange={(e) => handlePeriodChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-gray-800"
            >
              {periods.map((period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="text-center mb-4">
          <div className="text-xl font-bold bg-gray-50 p-3 rounded-lg border border-gray-200 text-gray-800">
            {`${displayHour}:${currentMinute} ${currentPeriod}`}
          </div>
        </div>

        <button
          onClick={handleConfirm}
          className="w-full py-2.5 bg-black text-white font-medium rounded-lg hover:bg-gray-800 transition-all duration-200"
        >
          Confirm Time
        </button>
      </div>
    </div>
  );
};

const AmenityBooking = ({ formData, setFormData, selectedService, onClose, onBookingSuccess }) => {
  const [subcategories, setSubcategories] = useState([]);
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [isBooking, setIsBooking] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasBooked, setHasBooked] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [itemUrgency, setItemUrgency] = useState({});

  // Initialize form with default values
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const defaultTime = '12:00';

    setFormData((prev) => ({
      ...prev,
      scheduledtime: prev.scheduledtime || `${today}T${defaultTime}:00.000Z`,
      room_number: prev.room_number || '',
    }));
  }, [setFormData]);

  // Fetch all items once
  useEffect(() => {
    const fetchAllItems = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityitems/`);
        const data = await response.json();
        setAllItems(data);
      } catch (error) {
        console.error('Error fetching all items:', error);
        toast.error('Failed to load items. Please try again.');
      }
    };
    fetchAllItems();
  }, []);

  // Fetch Subcategories
  useEffect(() => {
    const fetchSubcategories = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityservices/`);
        const data = await response.json();

        const filteredSubcategories = data.filter((sub) => sub.categoryid === selectedService?.id);
        setSubcategories(filteredSubcategories);

        if (filteredSubcategories.length > 0 && !selectedServiceId) {
          setSelectedServiceId(filteredSubcategories[0].id);
        }
      } catch (error) {
        console.error('Error fetching subcategories:', error);
        toast.error('Failed to load services. Please try again.');
      }
    };

    if (selectedService) {
      fetchSubcategories();
    }
  }, [selectedService]);

  // Filter items when service is selected
  useEffect(() => {
    if (selectedServiceId) {
      const filteredItems = allItems.filter((item) => item.serviceid == selectedServiceId);
      setItems(filteredItems);

      const urgencyMap = {};
      filteredItems.forEach((item) => {
        urgencyMap[item.id] = 'Normal';
      });
      setItemUrgency(urgencyMap);
    } else if (selectedService?.id) {
      const filteredItems = allItems.filter((item) => item.categoryid === selectedService.id);
      setItems(filteredItems);

      const urgencyMap = {};
      filteredItems.forEach((item) => {
        urgencyMap[item.id] = 'Normal';
      });
      setItemUrgency(urgencyMap);
    }
  }, [selectedServiceId, allItems, selectedService]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleDateChange = (e) => {
    const value = e.target.value;

    setFormData((prev) => {
      const currentTime = prev.scheduledtime?.split('T')[1]?.substring(0, 5) || '12:00';
      return {
        ...prev,
        scheduledtime: `${value}T${currentTime}:00.000Z`,
      };
    });

    if (fieldErrors.date) {
      setFieldErrors((prev) => ({ ...prev, date: '' }));
    }
  };

  const handleTimeConfirm = (time) => {
    const [hours, minutes] = time.split(':');

    setFormData((prev) => {
      const currentDate =
        prev.scheduledtime?.split('T')[0] || new Date().toISOString().split('T')[0];
      return {
        ...prev,
        scheduledtime: `${currentDate}T${hours}:${minutes}:00.000Z`,
      };
    });

    if (fieldErrors.time) {
      setFieldErrors((prev) => ({ ...prev, time: '' }));
    }
  };

  const formatTimeForDisplay = (timeString) => {
    if (!timeString) return 'Select Time';

    try {
      const timePart = timeString.split('T')[1]?.substring(0, 5);
      if (!timePart) return 'Select Time';

      const [hours, minutes] = timePart.split(':');
      const hourNum = parseInt(hours);
      const period = hourNum >= 12 ? 'PM' : 'AM';
      const displayHour = hourNum % 12 || 12;

      return `${displayHour}:${minutes} ${period}`;
    } catch (error) {
      return 'Select Time';
    }
  };

  const handleUrgencyChange = (itemId, urgency) => {
    setItemUrgency((prev) => ({
      ...prev,
      [itemId]: urgency,
    }));
  };

  const handleAddToCart = (item, quantity) => {
    if (quantity < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }

    const urgency = itemUrgency[item.id] || 'Normal';
    const price = urgency === 'Urgent' ? item.urgentprice : item.baseprice;

    const existingItem = cartItems.find((cartItem) => cartItem.itemid === item.id);

    if (existingItem) {
      const updatedCart = cartItems.map((cartItem) =>
        cartItem.itemid === item.id
          ? { ...cartItem, quantity: cartItem.quantity + quantity }
          : cartItem
      );
      setCartItems(updatedCart);
      toast.success(`Added ${quantity} more ${item.name}(s) to cart`);
    } else {
      const newItem = {
        itemid: item.id,
        item_name: item.name,
        quantity: quantity,
        urgencylevel: urgency,
        scheduledtime: formData.scheduledtime,
        specialinstructions: formData.specialinstructions || '',
        price: price,
      };
      setCartItems([...cartItems, newItem]);
      toast.success(`${item.name} added to cart`);
    }
  };

  const handleRemoveFromCart = (itemid) => {
    setCartItems(cartItems.filter((item) => item.itemid !== itemid));
  };

  const handleUpdateCartQuantity = (itemid, newQuantity) => {
    if (newQuantity < 1) {
      handleRemoveFromCart(itemid);
      return;
    }
    setCartItems(
      cartItems.map((item) => (item.itemid === itemid ? { ...item, quantity: newQuantity } : item))
    );
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.room_number || formData.room_number.trim() === '') {
      errors.room_number = 'Room number is required';
    }

    if (cartItems.length === 0) {
      errors.cart = 'Please add at least one item to your order';
    }

    if (!formData.scheduledtime) {
      errors.date = 'Please select a date';
      errors.time = 'Please select a time';
    } else {
      const datePart = formData.scheduledtime.split('T')[0];
      const timePart = formData.scheduledtime.split('T')[1]?.substring(0, 5);

      if (!datePart) {
        errors.date = 'Please select a date';
      }
      if (!timePart || timePart === '00:00') {
        errors.time = 'Please select a time';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;

    cartItems.forEach((item) => {
      const itemTotal = item.price * item.quantity;
      subtotal += itemTotal;
    });

    return {
      subtotal,
      grandTotal: subtotal,
    };
  };

  const totals = calculateTotals();

  const handleBookNow = async () => {
    if (hasBooked || isBooking) {
      return;
    }

    if (!validateForm()) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    const roomNumber = formData.room_number?.trim();
    if (!roomNumber) {
      toast.error('Room number is required');
      return;
    }

    setIsBooking(true);
    setHasBooked(true);

    try {
      const bulkItems = cartItems.map((item) => ({
        itemid: item.itemid,
        quantity: item.quantity,
        urgencylevel: item.urgencylevel,
        scheduledtime: formData.scheduledtime,
        specialinstructions: item.specialinstructions || 'No instructions',
      }));

      const requestData = {
        room_number: parseInt(roomNumber),
        items: bulkItems,
      };

      const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityrequests/by-room/with-billing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error('Failed to create service requests and billing');
      }

      const responseData = await response.json();

      // Store booking details
      const bookingDetails = {
        bookingid: responseData.bookingid,
        room_number: responseData.room_number,
        guest_name: responseData.guest_name,
      };

      const billingDetails = {
        billingid: responseData.billingid,
        total_base: responseData.total_base,
        total_tax: responseData.total_tax,
        finalamount: responseData.total_final,
      };

      sessionStorage.setItem('bookingDetails', JSON.stringify(bookingDetails));
      sessionStorage.setItem('billingDetails', JSON.stringify(billingDetails));
      sessionStorage.setItem(
        'selectedService',
        JSON.stringify({
          ...selectedService,
          amenity_name: selectedService?.name,
        })
      );

      if (onBookingSuccess) {
        onBookingSuccess({
          bookingDetails,
          billingDetails,
          response: responseData,
        });
      }

      // Reset cart
      setCartItems([]);

      // In handleBookNow function:
      toast.success('✅ Order Placed Successfully!', {
        position: 'top-right',
        autoClose: 3000,
        style: { zIndex: 999999 },
      });

      // Close modal AFTER toast is shown (delay)
      setTimeout(() => {
        setIsBooking(false);
        setHasBooked(false);
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(`❌ Booking failed: ${error.message}`, {
        autoClose: 5000,
        position: 'top-right',
      });
      setHasBooked(false);
      setIsBooking(false);
    }
  };
  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const QuantitySelector = ({ item, onAdd }) => {
    const [qty, setQty] = useState(1);
    const urgency = itemUrgency[item.id] || 'Normal';

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setQty(Math.max(1, qty - 1))}
            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center justify-center transition-all"
          >
            <IoRemove size={16} />
          </button>
          <span className="w-8 text-center text-gray-800 font-semibold">{qty}</span>
          <button
            onClick={() => setQty(qty + 1)}
            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center justify-center transition-all"
          >
            <IoAdd size={16} />
          </button>
          <button
            onClick={() => {
              onAdd(item, qty);
              setQty(1);
            }}
            className="ml-1 px-4 py-1.5 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-all"
          >
            Add
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => handleUrgencyChange(item.id, 'Normal')}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-all ${
              urgency === 'Normal'
                ? 'bg-black text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <IoTime size={12} />
            Normal
          </button>
          <button
            onClick={() => handleUrgencyChange(item.id, 'Urgent')}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-all ${
              urgency === 'Urgent'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <IoFlash size={12} />
            Urgent
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex-shrink-0 relative p-6 border-b border-gray-200">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{selectedService?.name}</h2>
                <p className="text-gray-500 text-sm mt-1">Select items and add to order</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 hover:bg-gray-100 rounded-lg"
              >
                <IoClose size={28} />
              </button>
            </div>
          </div>

          {/* Main Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
              {/* Items Section */}
              <div className="lg:col-span-2 space-y-4">
                {/* Room Number & Schedule */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Room Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="room_number"
                      value={formData.room_number || ''}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border ${
                        fieldErrors.room_number ? 'border-red-500' : 'border-gray-300'
                      } rounded-xl text-gray-800 placeholder-gray-400`}
                      placeholder="Enter room number"
                    />
                    {fieldErrors.room_number && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.room_number}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Service Type
                    </label>
                    <select
                      value={selectedServiceId}
                      onChange={(e) => setSelectedServiceId(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-800"
                    >
                      {subcategories.map((sub) => (
                        <option key={sub.id} value={sub.id}>
                          {sub.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.scheduledtime?.split('T')[0] || ''}
                      onChange={handleDateChange}
                      min={new Date().toISOString().split('T')[0]}
                      className={`w-full px-4 py-3 border ${
                        fieldErrors.date ? 'border-red-500' : 'border-gray-300'
                      } rounded-xl text-gray-800`}
                    />
                    {fieldErrors.date && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.date}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Time <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowTimePicker(true)}
                      className={`w-full px-4 py-3 border ${
                        fieldErrors.time ? 'border-red-500' : 'border-gray-300'
                      } rounded-xl text-gray-800 text-left hover:bg-gray-50 transition-all`}
                    >
                      {formatTimeForDisplay(formData.scheduledtime)}
                    </button>
                    {fieldErrors.time && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.time}</p>
                    )}
                  </div>
                </div>

                {/* Items Grid */}
                <div>
                  <h3 className="text-gray-800 font-semibold mb-3 flex items-center gap-2">
                    <span className="text-black">✦</span> Available Items
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className="bg-gray-50 border border-gray-200 rounded-xl p-4 hover:border-gray-400 transition-all group"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="text-gray-800 font-medium">{item.name}</h4>
                            <p className="text-gray-600 text-sm">
                              ₹
                              {itemUrgency[item.id] === 'Urgent'
                                ? item.urgentprice
                                : item.baseprice}
                              {itemUrgency[item.id] === 'Urgent' && (
                                <span className="text-red-500 text-xs ml-1">(urgent)</span>
                              )}
                            </p>
                          </div>
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <span className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded-full">
                              Available
                            </span>
                          </div>
                        </div>
                        <QuantitySelector item={item} onAdd={handleAddToCart} />
                      </div>
                    ))}
                  </div>
                  {items.length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                      No items available for this service
                    </div>
                  )}
                </div>
              </div>

              {/* Cart Section */}
              <div className="lg:col-span-1 bg-gray-50 rounded-xl border border-gray-200 p-4 h-fit sticky top-0">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-800 font-semibold flex items-center gap-2">
                    <IoCart className="text-black" />
                    Order Details
                  </h3>
                  <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-lg text-xs font-medium">
                    {getTotalItems()} items
                  </span>
                </div>

                {cartItems.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-3">🛒</div>
                    <p className="text-gray-500 text-sm">Cart is empty</p>
                    <p className="text-gray-400 text-xs mt-1">Add items from the list</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2 custom-scrollbar">
                      {cartItems.map((item) => (
                        <div
                          key={item.itemid}
                          className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="text-gray-800 text-sm font-medium">{item.item_name}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-gray-600 text-xs">₹{item.price} each</p>
                                {item.urgencylevel === 'Urgent' && (
                                  <span className="text-red-500 text-[10px] font-semibold bg-red-50 px-2 py-0.5 rounded">
                                    Urgent
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveFromCart(item.itemid)}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <IoClose size={16} />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={() =>
                                handleUpdateCartQuantity(item.itemid, item.quantity - 1)
                              }
                              className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center text-sm transition-all"
                            >
                              -
                            </button>
                            <span className="text-gray-800 text-sm w-6 text-center font-medium">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() =>
                                handleUpdateCartQuantity(item.itemid, item.quantity + 1)
                              }
                              className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center text-sm transition-all"
                            >
                              +
                            </button>
                            <span className="text-black font-semibold text-sm ml-auto">
                              ₹{item.price * item.quantity}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-gray-200 mt-4 pt-4 space-y-2">
                      <div className="flex justify-between text-gray-700 text-sm font-medium border-t border-dashed border-gray-200 pt-1">
                        <span>Subtotal</span>
                        <span>₹{totals.subtotal.toFixed(2)}</span>
                      </div>

                      <div className="flex justify-between text-gray-800 font-bold text-lg pt-2 border-t border-gray-200">
                        <span>Total</span>
                        <span className="text-black">₹{totals.grandTotal.toFixed(2)}</span>
                      </div>
                    </div>

                    {fieldErrors.cart && (
                      <p className="text-red-500 text-xs mt-2">{fieldErrors.cart}</p>
                    )}

                    <button
                      onClick={handleBookNow}
                      disabled={isBooking || hasBooked || cartItems.length === 0}
                      className={`w-full mt-4 py-3 rounded-xl font-semibold transition-all ${
                        isBooking || hasBooked || cartItems.length === 0
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-black text-white hover:bg-gray-800 shadow-lg'
                      }`}
                    >
                      {isBooking ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        `Place Order (${getTotalItems()} items)`
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <TimePickerModal
        isOpen={showTimePicker}
        onClose={() => setShowTimePicker(false)}
        onConfirm={handleTimeConfirm}
        currentTime={formData.scheduledtime?.split('T')[1]?.substring(0, 5) || '12:00'}
        title="Select Schedule Time"
      />
    </>
  );
};

export default AmenityBooking;

// import React, { useState, useEffect } from 'react';
// import { IoClose } from 'react-icons/io5';
// import { toast, ToastContainer } from 'react-toastify';
// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const AmenityBooking = ({
//   formData,
//   setFormData,
//   selectedService,
//   onClose,
//   confirmServiceBooking,
// }) => {
//   const [subcategories, setSubcategories] = useState([]);
//   const [items, setItems] = useState([]);
//   const [allItems, setAllItems] = useState([]);
//   const [previewData, setPreviewData] = useState(null);
//   const [showPreview, setShowPreview] = useState(false);
//   const [isBooking, setIsBooking] = useState(false);

//   // Fetch all items once when component mounts
//   useEffect(() => {
//     const fetchAllItems = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityitems/`);
//         const data = await response.json();
//         setAllItems(data);
//       } catch (error) {
//         console.error('Error fetching all items:', error);
//       }
//     };
//     fetchAllItems();
//   }, []);

//   useEffect(() => {
//     const fetchSubcategories = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityservices/`);
//         const data = await response.json();

//         // Filter services under the selected category
//         const filteredSubcategories = data.filter((sub) => sub.categoryid == selectedService?.id);

//         setSubcategories(filteredSubcategories);

//         // Reset serviceid when category changes
//         setFormData((prev) => ({ ...prev, serviceid: '' }));
//       } catch (error) {
//         console.error('Error fetching subcategories:', error);
//       }
//     };

//     if (selectedService) {
//       fetchSubcategories();
//     }
//   }, [selectedService, setFormData]);

//   useEffect(() => {
//     if (formData.serviceid) {
//       // Filter items based on service ID
//       const filteredItems = allItems.filter((item) => item.serviceid == formData.serviceid);
//       setItems(filteredItems);
//     } else if (selectedService?.id) {
//       // If no service selected but category is selected, show items for that category
//       const filteredItems = allItems.filter((item) => item.categoryid == selectedService.id);
//       setItems(filteredItems);
//     }
//   }, [formData.serviceid, allItems, selectedService]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handlePreview = async () => {
//     if (!formData.itemid || !formData.quantity || !formData.bookingid) {
//       alert('Please fill in booking ID, select an item and quantity first');
//       return;
//     }

//     try {
//       // Get the selected item to ensure we're previewing the correct one
//       const selectedItem = items.find((item) => item.id == formData.itemid);
//       if (!selectedItem) {
//         throw new Error('Selected item not found');
//       }

//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/amenityrequests/preview/${formData.itemid}/${formData.quantity}`
//       );
//       const data = await response.json();

//       // Verify the preview data matches the selected item
//       if (data.item_name !== selectedItem.name) {
//         console.warn("Preview data doesn't match selected item. Using local item data.");
//         data.base_amount = selectedItem.baseprice * formData.quantity;
//         data.urgent_amount = selectedItem.urgentprice * formData.quantity;
//         data.item_name = selectedItem.name;
//       }

//       setPreviewData(data);
//       setShowPreview(true);
//     } catch (error) {
//       console.error('Error fetching preview:', error);

//       // Fallback to local calculation if API fails
//       const selectedItem = items.find((item) => item.id == formData.itemid);
//       if (selectedItem) {
//         setPreviewData({
//           item_name: selectedItem.name,
//           base_amount: selectedItem.baseprice * formData.quantity,
//           urgent_amount: selectedItem.urgentprice * formData.quantity,
//           quantity: formData.quantity,
//         });
//         setShowPreview(true);
//       } else {
//         alert('Failed to fetch preview data');
//       }
//     }
//   };

//   const createBilling = async (bookingid, categoryName) => {
//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/create_amenity_service_billing?bookingid=${bookingid}&category_name=${encodeURIComponent(
//           categoryName
//         )}`,
//         {
//           method: 'POST',
//           headers: {
//             accept: 'application/json',
//           },
//         }
//       );

//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }

//       return await response.json();
//     } catch (error) {
//       console.error('Error creating billing:', error);
//       throw error;
//     }
//   };

//   const onBook = async () => {
//     if (!formData.bookingid) {
//       toast.error('Please enter a booking ID');
//       return;
//     }

//     // Get the selected item to calculate correct amount
//     const selectedItem = items.find((item) => item.id == formData.itemid);
//     if (!selectedItem) {
//       toast.error('Selected item not found. Please try again.');
//       return;
//     }

//     // Calculate the correct amount based on urgency level
//     const amount =
//       formData.urgencylevel === 'Urgent'
//         ? selectedItem.urgentprice * formData.quantity
//         : selectedItem.baseprice * formData.quantity;

//     // Prepare the request payload with correct amount
//     const requestBody = {
//       bookingid: formData.bookingid,
//       categoryid: selectedService?.id,
//       serviceid: formData.serviceid,
//       itemid: formData.itemid,
//       quantity: formData.quantity,
//       amount: amount,
//       urgencylevel: formData.urgencylevel || 'Normal',
//       scheduledtime: formData.scheduledtime,
//       specialinstructions: formData.specialinstructions || '',
//       status: 'Pending',
//     };

//     try {
//       // First create the amenity request
//       const amenityResponse = await fetch(`${BQ_BASE_URL}/bq/api/amenityrequests/`, {
//         method: 'POST',
//         headers: {
//           accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(requestBody),
//       });

//       if (!amenityResponse.ok) {
//         throw new Error(`HTTP error! status: ${amenityResponse.status}`);
//       }

//       const amenityData = await amenityResponse.json();
//       console.log('Amenity API Response:', amenityData);

//       // Then create the billing
//       const billingData = await createBilling(formData.bookingid, selectedService.name);
//       console.log('Billing API Response:', billingData);

//       // Show success toast for billing creation
//       toast.success('Billing created successfully!', {
//         position: 'bottom-right',
//         autoClose: 5000,
//       });

//       // Show overall success message
//       toast.success('Amenity service booked and billing created successfully!', {
//         position: 'bottom-right',
//         autoClose: 5000,
//       });

//       await confirmServiceBooking();
//       onClose();

//       return { amenityData, billingData };
//     } catch (error) {
//       console.error('Error making booking:', error);
//       toast.error(`Booking failed: ${error.message}`, {
//         position: 'bottom-right',
//         autoClose: 5000,
//       });
//       throw error;
//     }
//   };

//   const closePreview = () => {
//     setShowPreview(false);
//     setPreviewData(null);
//   };

//   const handleDateTimeChange = (e) => {
//     const { name, value } = e.target;

//     setFormData((prev) => {
//       const currentDate =
//         prev.scheduledtime?.split('T')[0] || new Date().toISOString().split('T')[0];
//       const currentTime = prev.scheduledtime?.split('T')[1]?.substring(0, 5) || '00:00';

//       let newDate = currentDate;
//       let newTime = currentTime;

//       if (name === 'date') {
//         newDate = value;
//       } else if (name === 'time') {
//         newTime = value;
//       }

//       return {
//         ...prev,
//         scheduledtime: `${newDate}T${newTime}:00.000Z`,
//       };
//     });
//   };

//   return (
//     <>
//       {/* Main Booking Modal */}
//       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
//         <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
//           <div className="p-6 relative">
//             <div className="flex justify-between items-start mb-6">
//               <div>
//                 <h2 className="text-2xl font-bold text-gray-800">Book {selectedService?.name}</h2>
//                 <p className="text-gray-500 text-sm mt-1">
//                   Fill in the details to schedule your service
//                 </p>
//               </div>
//               <button
//                 className="text-red-500 hover:text-red-700 transition-colors duration-200"
//                 onClick={onClose}
//               >
//                 <IoClose size={32} />
//               </button>
//             </div>

//             <input type="hidden" name="categoryid" value={selectedService?.id} />
//             <input type="hidden" name="status" value="Pending" />

//             <div className="grid grid-cols-2 gap-6">
//               <div className="col-span-2">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Booking ID <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="text"
//                   name="bookingid"
//                   value={formData.bookingid || ''}
//                   onChange={handleInputChange}
//                   className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                   placeholder="Enter your booking ID"
//                   required
//                 />
//               </div>

//               {/* Service and Item in one row */}
//               <div className="col-span-1">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Select a Service <span className="text-red-500">*</span>
//                 </label>
//                 <select
//                   name="serviceid"
//                   value={formData.serviceid}
//                   onChange={handleInputChange}
//                   className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                   required
//                 >
//                   <option value="" disabled>
//                     Select a service
//                   </option>
//                   {subcategories.map((sub) => (
//                     <option key={sub.id} value={sub.id}>
//                       {sub.name}
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               {/* Items Dropdown */}
//               {items.length > 0 && (
//                 <div className="col-span-1">
//                   <label className="block text-gray-700 text-sm font-medium mb-1">
//                     Select an Item <span className="text-red-500">*</span>
//                   </label>
//                   <select
//                     name="itemid"
//                     value={formData.itemid}
//                     onChange={handleInputChange}
//                     className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                     required
//                   >
//                     <option value="" disabled>
//                       Select an item
//                     </option>
//                     {items.map((item) => (
//                       <option key={item.id} value={item.id}>
//                         {item.name} - ₹
//                         {formData.urgencylevel === 'Urgent' ? item.urgentprice : item.baseprice}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//               )}

//               {/* Urgency Level and Quantity in one row */}
//               <div className="col-span-1">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Urgency Level
//                 </label>
//                 <select
//                   name="urgencylevel"
//                   value={formData.urgencylevel || 'Normal'}
//                   onChange={handleInputChange}
//                   className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                 >
//                   <option value="Normal">Normal</option>
//                   <option value="Urgent">Urgent</option>
//                 </select>
//               </div>

//               <div className="col-span-1">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Quantity <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="number"
//                   name="quantity"
//                   min="1"
//                   value={formData.quantity || 1}
//                   onChange={handleInputChange}
//                   className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                   required
//                 />
//               </div>

//               <div className="col-span-1">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Schedule Date <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="date"
//                   name="date"
//                   value={formData.scheduledtime?.split('T')[0] || ''}
//                   onChange={handleDateTimeChange}
//                   min={new Date().toISOString().split('T')[0]}
//                   className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                   required
//                 />
//               </div>

//               {/* Schedule Time */}
//               <div className="col-span-1">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Schedule Time <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="time"
//                   name="time"
//                   value={formData.scheduledtime?.split('T')[1]?.substring(0, 5) || ''}
//                   onChange={handleDateTimeChange}
//                   className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                   required
//                 />
//               </div>

//               {/* Special Instructions - full width */}
//               <div className="col-span-2">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Special Instructions
//                 </label>
//                 <textarea
//                   name="specialinstructions"
//                   value={formData.specialinstructions}
//                   onChange={handleInputChange}
//                   rows="3"
//                   className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                   placeholder="Any special requests or instructions"
//                 ></textarea>
//               </div>
//             </div>

//             {/* Buttons */}
//             <div className="mt-8 flex justify-end space-x-4">
//               <button
//                 onClick={handlePreview}
//                 className="px-10 py-2.5 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
//               >
//                 Preview
//               </button>
//               <button
//                 onClick={async () => {
//                   setIsBooking(true);
//                   try {
//                     await onBook();
//                   } catch (error) {
//                     // Error is already handled by the toast in onBook
//                   } finally {
//                     setIsBooking(false);
//                   }
//                 }}
//                 disabled={isBooking}
//                 className={`px-6 py-2.5 text-white font-semibold rounded-lg transition-colors duration-200 bg-black hover:text-gray-300 ${
//                   isBooking ? 'opacity-70 cursor-not-allowed' : ''
//                 }`}
//               >
//                 {isBooking ? (
//                   <span className="flex items-center justify-center">
//                     <svg
//                       className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                       xmlns="http://www.w3.org/2000/svg"
//                       fill="none"
//                       viewBox="0 0 24 24"
//                     >
//                       <circle
//                         className="opacity-25"
//                         cx="12"
//                         cy="12"
//                         r="10"
//                         stroke="currentColor"
//                         strokeWidth="4"
//                       ></circle>
//                       <path
//                         className="opacity-75"
//                         fill="currentColor"
//                         d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                       ></path>
//                     </svg>
//                     Processing...
//                   </span>
//                 ) : (
//                   'Book Now'
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Preview Modal */}
//       {showPreview && (
//         <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
//           <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-scaleIn">
//             <div className="relative">
//               {/* Header with gradient background */}
//               <div className="bg-gradient-to-r from-black to-gray-700 p-6">
//                 <div className="flex justify-between items-start">
//                   <div>
//                     <h2 className="text-2xl font-bold text-white">Order Summary</h2>
//                     <p className="text-blue-100 text-sm mt-1">
//                       Review your order before confirmation
//                     </p>
//                   </div>
//                   <button
//                     className="text-white hover:text-blue-200 transition-colors duration-200 p-1 rounded-full hover:bg-white/10"
//                     onClick={closePreview}
//                   >
//                     <IoClose size={24} />
//                   </button>
//                 </div>
//               </div>

//               {/* Content */}
//               <div className="p-6 space-y-5">
//                 {/* Booking ID */}
//                 <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
//                   <div className="flex justify-between">
//                     <span className="text-gray-600">Booking ID:</span>
//                     <span className="font-medium text-gray-700">{formData.bookingid}</span>
//                   </div>
//                 </div>

//                 {/* Order Items Card */}
//                 <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
//                   <div className="flex items-center space-x-3 mb-3">
//                     <div className="bg-blue-100 p-2 rounded-lg">
//                       <svg
//                         className="w-6 h-6 text-blue-600"
//                         fill="none"
//                         stroke="currentColor"
//                         viewBox="0 0 24 24"
//                       >
//                         <path
//                           strokeLinecap="round"
//                           strokeLinejoin="round"
//                           strokeWidth={2}
//                           d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
//                         />
//                       </svg>
//                     </div>
//                     <div>
//                       <h3 className="font-semibold text-gray-800">{previewData?.item_name}</h3>
//                       <p className="text-sm text-gray-500">Qty: {previewData?.quantity}</p>
//                     </div>
//                   </div>

//                   <div className="space-y-3 pt-3 border-t border-gray-200">
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Urgency:</span>
//                       <span
//                         className={`font-medium ${
//                           formData.urgencylevel === 'Urgent' ? 'text-red-500' : 'text-gray-700'
//                         }`}
//                       >
//                         {formData.urgencylevel || 'Normal'}
//                       </span>
//                     </div>
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Scheduled for:</span>
//                       <span className="font-medium text-gray-700">
//                         {new Date(formData.scheduledtime).toLocaleString()}
//                       </span>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Pricing Breakdown */}
//                 <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
//                   <h4 className="font-medium text-gray-700 mb-3">Pricing Breakdown</h4>
//                   <div className="space-y-2">
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Base Amount</span>
//                       <span className="font-medium">₹{previewData?.base_amount}</span>
//                     </div>
//                     {formData.urgencylevel === 'Urgent' && (
//                       <div className="flex justify-between">
//                         <span className="text-gray-600">Urgent Surcharge</span>
//                         <span className="font-medium text-red-500">
//                           +₹
//                           {previewData?.urgent_amount - previewData?.base_amount}
//                         </span>
//                       </div>
//                     )}
//                     <div className="pt-2 border-t border-gray-200 flex justify-between">
//                       <span className="font-semibold text-gray-800">Total</span>
//                       <span className="font-bold text-blue-600">
//                         ₹
//                         {formData.urgencylevel === 'Urgent'
//                           ? previewData?.urgent_amount
//                           : previewData?.base_amount}
//                       </span>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Special Instructions */}
//                 {formData.specialinstructions && (
//                   <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
//                     <h4 className="font-medium text-gray-700 mb-2">Special Instructions</h4>
//                     <p className="text-gray-700 italic">"{formData.specialinstructions}"</p>
//                   </div>
//                 )}
//               </div>

//               {/* Footer with action buttons */}
//               <div className="px-6 pb-6 pt-2 bg-gray-50 border-t border-gray-100">
//                 <button
//                   onClick={closePreview}
//                   className="w-full py-3 px-4 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors duration-200 mb-3"
//                 >
//                   Back to Edit
//                 </button>
//                 <button
//                   onClick={async () => {
//                     setIsBooking(true);
//                     try {
//                       await onBook();
//                       closePreview();
//                     } catch (error) {
//                       // Error is already handled by the toast in onBook
//                     } finally {
//                       setIsBooking(false);
//                     }
//                   }}
//                   disabled={isBooking}
//                   className={`w-full py-3 px-4 text-white font-semibold rounded-lg transition-colors duration-200 bg-black hover:text-gray-300 ${
//                     isBooking ? 'opacity-70 cursor-not-allowed' : ''
//                   }`}
//                 >
//                   {isBooking ? 'Processing...' : 'Confirm Booking'}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//       <ToastContainer position="bottom-right" autoClose={5000} />
//     </>
//   );
// };

// export default AmenityBooking;
